<template>
  <div
    id="main-onboarding-video-widget"
    ref="draggable"
    class="tw:bg-no-repeat tw:bg-center tw:bg-[url('https://d3vhsxl1pwzf0p.cloudfront.net/marketingmachine/edrone_loader.gif')] tw:bg-default-200 tw:fixed tw:flex tw:group tw:bottom-0 tw:max-h-0 tw:max-w-0 tw:h-[95%] tw:w-[95%] tw:z-[99999999999]"
    @mousedown.stop.prevent="startDragging"
  >
    <div
      class="tw:group-hover:flex tw:group-active:flex tw:flex-col tw:justify-between tw:hidden tw:w-full tw:absolute tw:p-2 tw:gap-2 tw:h-full"
    >
      <div class="tw:flex tw:justify-between tw:w-full">
        <div
          class="tw:size-8 tw:justify-center tw:items-center tw:flex tw:bg-white tw:hover:bg-default-300 tw:active:bg-default-300 tw:z-10 tw:p-1 tw:rounded-lg"
          @click="changeSize"
        >
          <font-awesome-icon
            :icon="size === 'small' ? 'fa-light fa-expand' : 'fa-light fa-compress'"
            class="tw:size-4 tw:rotate-90"
          />
        </div>
        <div
          class="tw:size-8 tw:justify-center tw:flex tw:hover:bg-default-300 tw:active:bg-default-300 tw:items-center tw:bg-white tw:z-10 tw:p-1 tw:rounded-lg"
          @click.stop="$emit('close')"
        >
          <font-awesome-icon class="tw:size-4" icon="fa-light fa-window-close" />
        </div>
      </div>

      <div
        class="tw:size-8 tw:justify-center tw:flex tw:hover:bg-default-300 tw:active:bg-default-300 tw:items-center tw:bg-white tw:z-10 tw:p-1 tw:rounded-lg"
        @click.stop="videoPlayer?.paused ? playVideo() : pauseVideo()"
      >
        <font-awesome-icon v-if="isPaused" class="tw:size-4" icon="fa-light fa-play" />
        <font-awesome-icon v-else class="tw:size-4" icon="fa-light fa-pause" />
      </div>
    </div>
    <video :key="videoUrl" ref="videoPlayer" autoplay height="100%" width="100%">
      <source :src="videoUrl" />
    </video>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, Ref, ref } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

interface Props {
  videoUrl?: string;
}
withDefaults(defineProps<Props>(), {
  videoUrl: "",
});
defineEmits(["close"]);

const draggable: Ref<null | HTMLDivElement> = ref(null);
const position = ref({
  clientX: 0,
  clientY: 0,
  movX: 0,
  movY: 0,
});
const isDragging = ref(false);
const size = ref<"small" | "large">("small");
const videoPlayer: Ref<null | HTMLVideoElement> = ref(null);

const proportion = ref<number>(16 / 9);

onMounted(() => {
  const videoHeight = videoPlayer.value?.videoHeight;
  const videoWidth = videoPlayer.value?.videoWidth;
  if (draggable.value) {
    if (videoHeight && videoWidth) {
      proportion.value = Number(videoWidth / videoHeight);
    }
    draggable.value.style.maxWidth = proportion.value * (size.value === "small" ? 22.5 : 45) + "rem";
    draggable.value.style.maxHeight = size.value === "small" ? "22.5rem" : "45rem";
  }
});

const startDragging = (e: MouseEvent) => {
  position.value.clientX = e.clientX;
  position.value.clientY = e.clientY;
  document.onmousemove = dragElement;
  document.onmouseup = stopDragging;
  document.onmouseleave = stopDragging;
};

const getValueX = (offset: number) => {
  return Math.min(Math.max(0, offset), window.innerWidth - (draggable.value?.clientWidth || 300));
};

const getValueY = (offset: number) => {
  return Math.min(Math.max(0, offset), window.innerHeight - (draggable.value?.clientHeight || 300));
};

const dragElement = (e: MouseEvent) => {
  isDragging.value = true;
  position.value.movX = position.value.clientX - e.clientX;
  position.value.movY = position.value.clientY - e.clientY;
  position.value.clientX = e.clientX;
  position.value.clientY = e.clientY;

  if (draggable.value) {
    draggable.value.style.cursor = "grabbing";
    setPosition();
  }
};

const setPosition = () => {
  if (draggable.value) {
    const topPx = getValueY(draggable.value.offsetTop - position.value.movY);
    const leftPx = getValueX(draggable.value.offsetLeft - position.value.movX);

    draggable.value.style.top = `${topPx / 16}rem`;
    draggable.value.style.left = `${leftPx / 16}rem`;
  }
};

const stopDragging = async () => {
  if (draggable.value) {
    draggable.value.style.cursor = "pointer";
  }
  document.onmouseup = null;
  document.onmousemove = null;
};

const isPaused = ref(false);
const pauseVideo = () => {
  videoPlayer.value?.pause();
  isPaused.value = true;
};
const playVideo = () => {
  videoPlayer.value?.play();
  isPaused.value = false;
};

const changeSize = () => {
  size.value = size.value === "small" ? "large" : "small";

  if (draggable.value) {
    draggable.value.style.maxWidth = proportion.value * (size.value === "small" ? 22.5 : 45) + "rem";
    draggable.value.style.maxHeight = size.value === "small" ? "22.5rem" : "45rem";
    setPosition();
  }
};
</script>
